import React, { useCallback, useMemo, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';

// Providers
import { AuthProvider, useAuth } from './context/AuthContext';
import RefreshProvider from './context/RefreshContext';
import ProtectedRoute from './components/ProtectedRoute';

// Layout components
import Layout from './components/Layout';

// Pages
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Sites from './pages/Sites';
import Devices from './pages/Devices';
import SiteDetails from './pages/SiteDetails';
import DeviceDetails from './pages/DeviceDetails';
import BillingReports from './pages/BillingReports';
import Settings from './pages/Settings';
import UserProfile from './pages/UserProfile';
import Users from './pages/Users';
import Notifications from './pages/Notifications';
import Monitor from './pages/Monitor';
import NotFound from './pages/NotFound';
import DeviceInviteSignup from './pages/DeviceInviteSignup';

import { ColorModeContext } from './context/ColorModeContext';

// Root redirect component to handle authentication
const RootRedirect = () => {
  const { user, loading } = useAuth();
  
  // While checking authentication, return null (or a loading spinner)
  if (loading) return null;
  
  // If not authenticated, redirect to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // If authenticated, redirect based on role
  if (user.mustChangePassword) {
    return <Navigate to="/profile" replace />;
  }

  if (user.role === 'admin' || user.role === 'operator' || user.role === 'super_user' || user.role === 'end_user') {
    return <Navigate to="/sites" replace />;
  }
  
  // Fallback
  return <Navigate to="/login" replace />;
};

function App() {
  const getInitialMode = () => {
    try {
      const stored = localStorage.getItem('colorMode');
      if (stored === 'light' || stored === 'dark') return stored;
    } catch (e) {
      // ignore
    }
    return 'light';
  };

  const [mode, setModeState] = useState(getInitialMode);

  const setMode = useCallback((nextMode) => {
    setModeState(nextMode);
    try {
      localStorage.setItem('colorMode', nextMode);
    } catch (e) {
      // ignore
    }
  }, []);

  const toggleColorMode = useCallback(() => {
    setMode((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, [setMode]);

  const theme = useMemo(() => {
    return createTheme({
      palette: {
        mode,
        primary: {
          main: '#2196f3',
          light: '#6ec6ff',
          dark: '#0069c0'
        },
        secondary: {
          main: '#00bcd4',
          light: '#62efff',
          dark: '#008ba3'
        },
        background: mode === 'dark'
          ? {
              default: '#121212',
              paper: '#1e1e1e'
            }
          : {
              default: '#f5f5f5',
              paper: '#ffffff'
            }
      },
      typography: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        h1: { fontWeight: 500 },
        h2: { fontWeight: 500 },
        h3: { fontWeight: 500 }
      },
      components: {
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: 8,
              boxShadow: mode === 'dark'
                ? '0 4px 10px rgba(0, 0, 0, 0.5)'
                : '0 4px 6px rgba(0, 0, 0, 0.1)'
            }
          }
        }
      }
    });
  }, [mode]);

  return (
    <ColorModeContext.Provider value={{ mode, setMode, toggleColorMode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <RefreshProvider>
            <Box sx={{ display: 'flex', height: '100vh' }}>
              <Routes>
              {/* Public route */}
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/device-invite/:token" element={<DeviceInviteSignup />} />
              
              {/* Root path redirect */}
              <Route path="/" element={<RootRedirect />} />
              
              {/* Protected routes */}
              <Route element={<ProtectedRoute />}>
                <Route element={<Layout />}>
                  {/* Sites - accessible by admin and super_user */}
                  <Route 
                    path="/sites" 
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'super_user', 'end_user', 'operator']}>
                        <Sites />
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* Site routes - accessible by admin and super_user */}
                  <Route 
                    path="/sites/:id" 
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'super_user', 'end_user', 'operator']}>
                        <SiteDetails />
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* Device routes - accessible by all users */}
                  <Route 
                    path="/devices" 
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'super_user', 'end_user', 'operator']}>
                        <Devices />
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* Users routes - accessible by admin and super_user only */}
                  <Route 
                    path="/users" 
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'super_user', 'operator']}>
                        <Users />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/devices/:id" 
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'super_user', 'end_user', 'operator']}>
                        <DeviceDetails />
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* Accounts route - account-linked admin/super_user users access this via nav */}
                  <Route 
                    path="/accounts" 
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'super_user', 'operator']}>
                        <BillingReports />
                      </ProtectedRoute>
                    } 
                  />

                  {/* Billing route kept as backward-compatible alias */}
                  <Route 
                    path="/billing" 
                    element={
                      <Navigate to="/accounts" replace />
                    } 
                  />
                  
                  {/* Settings route - accessible by all authenticated users */}
                  <Route 
                    path="/settings" 
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'operator']}>
                        <Settings />
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* Notifications route - accessible by admin only */}
                  <Route 
                    path="/notifications" 
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'operator']}>
                        <Notifications />
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* Cross-site monitor (leaks, thresholds, poor signal) */}
                  <Route 
                    path="/monitor" 
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'super_user', 'operator']}>
                        <Monitor />
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* User Profile route - accessible by all users */}
                  <Route 
                    path="/profile" 
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'super_user', 'end_user', 'operator']}>
                        <UserProfile />
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* 404 route */}
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Route>
              </Routes>
            </Box>
          </RefreshProvider>
        </AuthProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
